import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Group, Stack, Text, UnstyledButton} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import navigateToURL from 'lib/navigateToURL';
import AIMenu from 'llm/chat-library/ui/AIMenu';
import {AI_GRAY_CHEVRON} from 'llm/chat-library/ui/AIStyleConstants';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import {agentConfigurationUrl} from 'llm/routes/UrlUtils';
import useRoutine from 'llm/routines/useRoutine';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import type {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import Section from 'ui-library/menus/Section';
import * as DateUtil from 'util/DateUtil';

import {redesignHeaderTitleButtonStyles, redesignHeaderTitleMenuAnchorStyles} from './RedesignHeaderIconChip';

const useStyles = createStyles((theme) => ({
  options: redesignHeaderTitleButtonStyles(theme),
  menuAnchor: redesignHeaderTitleMenuAnchorStyles,
}));

/** Opens the agent's configuration in the chat settings modal, which portals out of the sidepanel. */
export function navigateToAgentConfiguration(routineId: string): void {
  navigateToURL(agentConfigurationUrl(routineId));
}

/**
 * The header title for the Agent homepage: the agent's name as the page's level-1 heading, with a menu
 * for the actions that used to live in the header. Renders nothing until the routine resolves, so
 * callers can place it in a plain layout slot.
 */
export function RoutineHomepageHeaderTitle({routineId}: {routineId: string}): JSX.Element | null {
  const {classes} = useStyles();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {routine} = useRoutine({routineId});
  const handleOpenMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);
  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const menuOptions = useMemo(() => {
    const options: Array<OverflowMenuOption> = [];
    if (isAgentsFeatureEnabled()) {
      options.push({
        icon: 'sliders',
        contents: <>Settings</>,
        dataTestAttrs: {
          component: 'RoutineHomepageHeaderTitle',
          element: 'settingsMenuItem',
        },
        onSelect: () => {
          navigateToAgentConfiguration(routineId);
        },
      });
    }
    options.push({
      icon: 'receipt',
      contents: <>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</>,
      dataTestAttrs: {
        component: 'RoutineHomepageHeaderTitle',
        element: 'exportAuditLogMenuItem',
      },
      onSelect: () => {
        exportAuditLogs([routineId]);
      },
    });
    return options;
  }, [routineId]);

  const menu = useMemo(
    () => (
      <AIMenu scrollable={false} height={MenuHeight.SMALL} width={MenuWidth.FIT_CONTENT}>
        <Section>
          {menuOptions.map((option, index) => (
            <Option
              key={index}
              icon={option.icon}
              dataTestAttrs={option.dataTestAttrs}
              onSelect={() => {
                option.onSelect?.();
                setIsMenuOpen(false);
              }}
            >
              {option.contents}
            </Option>
          ))}
        </Section>
      </AIMenu>
    ),
    [menuOptions]
  );

  if (routine == null) {
    return null;
  }
  const name = routine.name ?? 'Agent';
  const createdOn = DateUtil.readableTime(routine.createdAt, {showTime: false});

  return (
    <div {...getTestProps({component: 'RoutineHomepageHeaderTitle', element: 'root'})}>
      <Stack align="center" aria-label={name} aria-level={1} miw={0} role="heading" spacing={0}>
        <AbsoluteDropdownContainer
          className={classes.menuAnchor}
          menu={menu}
          onRequestClose={handleCloseMenu}
          open={isMenuOpen}
          shouldCloseOnInsideClick={true}
          shouldCloseOnOutsideClick={true}
          canDropUnder={false}
          alwaysDropDown={true}
        >
          <UnstyledButton
            {...getTestProps({component: 'RoutineHomepageHeaderTitle', element: 'menuTrigger'})}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-label="Agent options"
            className={classes.options}
            maw="100%"
            miw={0}
            onClick={handleOpenMenu}
          >
            <Stack align="center" maw="100%" miw={0} spacing={0}>
              <Group maw="100%" miw={0} noWrap={true} spacing="xs">
                <Text component="span" fw={600} miw={0} size="md" sx={{flex: 1}} truncate={true}>
                  {name}
                </Text>
                <BenchlingIcon color={AI_GRAY_CHEVRON} icon="chevron-down" />
              </Group>
              <Text c="dimmed" size="xs">
                Created on {createdOn}
              </Text>
            </Stack>
          </UnstyledButton>
        </AbsoluteDropdownContainer>
      </Stack>
    </div>
  );
}
